/**
 * syncDirtyProfiles
 *
 * Attempts to push any locally-modified ("dirty") profiles to the backend
 * before the session is terminated.  Resolves once:
 *   - All dirty profiles have been synced (success), OR
 *   - The operation times out (MAX_WAIT_MS), so sign-out is never blocked
 *     indefinitely by a slow or unavailable network.
 *
 * Safe to call from any platform — exits immediately if the profileStore or
 * its sync methods are unavailable (e.g. on mobile where this utility is not
 * yet wired up).
 */

const MAX_WAIT_MS = 8_000; // 8 seconds max before we give up and sign out anyway

export async function syncDirtyProfiles(): Promise<void> {
  const profileStore = (globalThis as any).__profileStore;
  if (!profileStore?.getState) return;

  const state = profileStore.getState();
  const getDirty: (() => any[]) | undefined = state?.getDirtyProfiles;
  const retry: (() => Promise<void>) | undefined = state?.retryDirtyProfiles;

  if (!getDirty || !retry) return;

  const dirty = getDirty();
  if (dirty.length === 0) return;

  console.log(`🔄 syncDirtyProfiles: syncing ${dirty.length} dirty profile(s) before sign-out…`);

  const timeout = new Promise<void>(resolve => setTimeout(resolve, MAX_WAIT_MS));

  try {
    await Promise.race([retry(), timeout]);
    console.log('✅ syncDirtyProfiles: pre-sign-out sync complete');
  } catch (err) {
    // Non-fatal — sign-out proceeds regardless
    console.warn('⚠️ syncDirtyProfiles: sync attempt failed, proceeding with sign-out:', err);
  }
}
